import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterMedia, getHotMedia } from "@/utils/media";
import { typeLabels } from "@/utils/labels";
import type { MediaType } from "@/types/media";

const typeTabs: { key: MediaType | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [activeType, setActiveType] = useState<MediaType | "all">("all");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setActiveType("all");
  }, [keyword]);

  usePageMeta({
    title: keyword
      ? `“${keyword}”的搜索结果 - 17C.COM`
      : "影视搜索 - 17C.COM",
    description: keyword
      ? `在 17C.COM 搜索“${keyword}”的相关影视作品，查看匹配的电影、电视剧与综艺内容。`
      : "17C.COM 影视搜索，输入影视名称、类型或演员，快速找到想看的内容。",
    keywords: keyword
      ? `${keyword},影视搜索,${typeLabels.movie},${typeLabels.tv},17C.COM`
      : "影视搜索,影视查找,17C.COM",
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const allResults = useMemo(() => {
    if (!keyword.trim()) return [];
    return filterMedia({ keyword, type: "all", sort: "最热" });
  }, [keyword]);

  const results = useMemo(() => {
    if (activeType === "all") return allResults;
    return allResults.filter((item) => item.type === activeType);
  }, [allResults, activeType]);

  const recommendations = getHotMedia(6);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <header className="mb-6">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          影视搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入影视名称、类型或演员，快速定位你想看的作品
        </p>
      </header>

      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i
              className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：星际、悬疑、长安夜行"
              aria-label="搜索关键词"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-8 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-search-line" aria-hidden="true" />
            搜索
          </button>
        </div>
      </form>

      {keyword ? (
        <>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              关键词 <span className="font-bold text-primary-600">“{keyword}”</span>
              ，共找到{" "}
              <span className="font-bold text-primary-600">{results.length}</span> 个结果
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {typeTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveType(tab.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab.key === activeType
                      ? "bg-primary-600 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {results.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              <EmptyState
                icon="ri-search-eye-line"
                title="未找到相关影视作品"
                description={`没有找到与“${keyword}”匹配的内容，换个关键词试试，或看看下面的热门推荐。`}
              />
              <div>
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                  热门影视推荐
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                  {recommendations.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-lightbulb-line text-accent-500" aria-hidden="true" />
              搜索小贴士
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {["星际", "悬疑", "古装", "长安夜行", "科幻", "动画"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSearchParams({ q: tag })}
                  className="rounded-full bg-background-100 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-line text-accent-500" aria-hidden="true" />
              大家都在看
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {recommendations.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}